import {
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { Field, Toggle } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type { ApiError, ExtensionInstance } from '@mangovoice/types'

import { SetActiveStateDialog } from '../../components/SetActiveStateDialog'
import {
  useClearDialNow,
  useCreateExtension,
  useGetExtensionInfo,
  useListExtensions,
  useLocationsSearch,
  useMobileDevices,
  usePbxCallerIdOptions,
  usePbxSoundClips,
  usePbxSystemDefaults,
  useResetSipSecret,
  useSetLineActiveState,
  useUpdateExtension,
  useVoipProfiles,
} from '../../hooks'
import { useExtensionsI18n } from '../../i18n/provider'
import type { UpdateExtensionPayload } from '../../types/api.types'
import type { LineSeedFields } from '../../types/extension.types'
import { isInactiveExtension } from '../../types/extension.types'
import {
  buildLineCreatePayload,
  CALL_RECORDING_OPTIONS,
  DEFAULT_VOICEMAIL_TIMEZONE,
  EXTENSION_TYPE_DEFAULT_NUMBER,
  FINDME_MODE_OPTIONS,
  FORWARDING_NUMBER_REGEX,
  getNextExtensionNumber,
  isReservedExtensionNumber,
  isValidEmailList,
  normalizeFindMeDelay,
  normalizeFindMeMode,
  normalizeOptionalPhoneNumber,
  normalizeToLegacyTimezone,
  SWITCH_FORM_CSS,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
  VOICEMAIL_TIMEZONE_OPTIONS,
} from '../../utils'
import { generateSecureFourDigitPin } from '../../utils/generateSecureFourDigitPin'
import { getListLabelForSlug, getTypeLabelForSlug } from '../typeSlugLabels'
import { renderWithWrapper } from './renderWithWrapper'
import type { ExtensionEditPageProps } from './types'

const LEGACY_PREFERRED_CODEC_OPTIONS = [
  { value: 'OPUS' },
  { value: 'PCMU' },
  { value: 'G722' },
  { value: 'G729' },
] as const

const FINDME_DELAY_OPTIONS = [
  { value: 0, label: 'Immediately' },
  { value: 5, label: '5 Seconds' },
  { value: 10, label: '10 Seconds' },
  { value: 15, label: '15 Seconds' },
] as const
const LEGACY_BLOCKED_AREA_CODES = new Set([
  '911',
  '899',
  '888',
  '877',
  '866',
  '855',
  '844',
  '833',
  '822',
  '800',
  '611',
  '411',
])

const toI18nKey = (value: string) => value.replace(/[^\w]/g, '_').toLowerCase()

const toDigitsOnly = (value: string) => value.replace(/\D/g, '')

const phoneDigitsMatch = (a: string, b: string): boolean => {
  const dA = toDigitsOnly(a)
  const dB = toDigitsOnly(b)
  if (!dA || !dB) return false
  if (dA === dB) return true
  return dA.endsWith(dB) || dB.endsWith(dA)
}

const formatDialNowSummary = (
  rawDialNow:
    | string
    | {
        cellular?: string | null
        dial_now?: string | null
      }
    | null
    | undefined,
) => {
  if (!rawDialNow) return ''
  if (typeof rawDialNow === 'string') {
    return rawDialNow.trim()
  }

  const cellular = rawDialNow.cellular?.trim() ?? ''
  const dialNow = rawDialNow.dial_now?.trim() ?? ''
  if (cellular && dialNow) return `Cell: ${cellular}, Dial Now: ${dialNow}`
  return cellular || dialNow
}

export const LineEditPage = ({
  pbxId,
  type,
  extensionId: extensionIdParam,
  toast,
  getListRoute,
  wrapper,
  isMangoInternal = false,
  isUkEnvironment = false,
}: ExtensionEditPageProps) => {
  const { text } = useExtensionsI18n()
  const navigate = useNavigate()
  const pbxIdNum = pbxId
  const isAddMode = extensionIdParam === 'new'
  const extensionId = !isAddMode ? parseInt(extensionIdParam, 10) : undefined
  const isValidId =
    !isAddMode && extensionId != null && !Number.isNaN(extensionId)

  const {
    data: extension,
    isLoading,
    isError,
  } = useGetExtensionInfo({
    extensionId: extensionId ?? 0,
    options: { enabled: isValidId },
  })

  const { locations: pbxLocationOptions } = useLocationsSearch(pbxIdNum, '')
  const { extensions: allExtensions, isLoading: isLoadingAllExtensions } =
    useListExtensions({
      pbxId: pbxIdNum,
      listParams: { limit: 1000, fetchAll: true },
      options: { enabled: isAddMode },
    })
  const { profiles: pbxProfiles, isLoading: isLoadingProfiles } =
    useVoipProfiles(pbxIdNum)
  // Ensure the line's currently-saved profile is always selectable, even if
  // the profiles list doesn't include it (e.g. an orphaned/deactivated
  // profile). Without this, editing an unrelated field would surface the
  // assignment as "None" and the required-field check would block saving.
  const profileOptions = useMemo(() => {
    const saved = (
      extension as (ExtensionInstance & LineSeedFields) | undefined
    )?.assigned_profile
    if (saved?.id == null) return pbxProfiles
    if (pbxProfiles.some((p) => p.id === saved.id)) return pbxProfiles
    return [
      { id: saved.id, name: saved.name, subject_type: 'user' as const },
      ...pbxProfiles,
    ]
  }, [pbxProfiles, extension])
  const { systemDefaults } = usePbxSystemDefaults(pbxIdNum)
  const { soundClips } = usePbxSoundClips(pbxIdNum)
  const { callerIdOptions, isLoading: isLoadingCallerIdOptions } =
    usePbxCallerIdOptions(pbxIdNum)

  const updateMutation = useUpdateExtension(pbxIdNum)
  const createMutation = useCreateExtension(pbxIdNum)
  const resetSipSecretMutation = useResetSipSecret(extensionId, pbxIdNum)
  const clearDialNowMutation = useClearDialNow(extensionId, pbxIdNum)
  const setActiveStateMutation = useSetLineActiveState(extensionId, pbxIdNum)
  const { data: mobileDevices = [] } = useMobileDevices(
    isMangoInternal && !isAddMode ? (extensionId ?? null) : null,
  )

  const [activeStateDialogOpen, setActiveStateDialogOpen] = useState(false)
  const [activeStateDialogTarget, setActiveStateDialogTarget] =
    useState<boolean>(true)
  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [assignedProfileId, setAssignedProfileId] = useState<string>('')
  const [locationId, setLocationId] = useState<string>('')
  const [outboundExtension, setOutboundExtension] = useState<string>('0')
  const [outboundLocalAreaCode, setOutboundLocalAreaCode] = useState('')
  const [outboundExternalCallerId, setOutboundExternalCallerId] = useState('')
  const [voicemailEnabled, setVoicemailEnabled] = useState(isAddMode)
  const [voicemailTimeZone, setVoicemailTimeZone] = useState(
    DEFAULT_VOICEMAIL_TIMEZONE,
  )
  const [voicemailPassword, setVoicemailPassword] = useState(
    isAddMode ? generateSecureFourDigitPin() : '',
  )
  const [sendEmailNotifications, setSendEmailNotifications] = useState(false)
  const [attachVoicemailMessage, setAttachVoicemailMessage] = useState(false)
  const [deleteMessageAfterSend, setDeleteMessageAfterSend] = useState(false)
  const [messageEmailAddress, setMessageEmailAddress] = useState('')
  const [useSystemGreetings, setUseSystemGreetings] = useState(true)
  const [outgoingMessageUnavailableId, setOutgoingMessageUnavailableId] =
    useState<string>('')
  const [findMeMode, setFindMeMode] = useState('0')
  const [findMeDelay, setFindMeDelay] = useState<number>(0)
  const [findMeCellphone, setFindMeCellphone] = useState('')
  const [omitFromDirectory, setOmitFromDirectory] = useState(false)
  const [forwardingNumber, setForwardingNumber] = useState('')
  const [localAreaCodeTouched, setLocalAreaCodeTouched] = useState(false)
  const [t38Device, setT38Device] = useState(false)
  const [preferredCodec, setPreferredCodec] = useState('OPUS')
  const [inCallStarCodesEnabled, setInCallStarCodesEnabled] = useState(true)
  const [callRecording, setCallRecording] = useState('2')
  const [dialNowSummary, setDialNowSummary] = useState('')
  const [isLineDetailsCollapsed, setIsLineDetailsCollapsed] = useState(true)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const localAreaCodeAutofilledKeyRef = useRef<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const createAnotherRef = useRef(false)
  // Tracks which extension's assigned profile has been seeded so a background
  // profiles refetch (tab focus, etc.) can't overwrite an in-progress
  // dropdown selection with the saved value on re-run.
  const assignedProfileSeededKeyRef = useRef<number | string | null>(null)
  // Tracks whether the user explicitly changed the caller-ID select in this
  // edit session. Used to gate the snap-to-first-option effect and the
  // submit overwrite, so unrelated edits cannot wipe a previously-saved
  // caller ID just because options haven't loaded yet or the saved value
  // failed to match a rendered option.
  const outboundExternalCallerIdTouchedRef = useRef(false)
  // Snapshot of the originally-loaded server value, kept in state so that
  // the validation/warning render can react to it. `undefined` means "not
  // loaded yet" (don't surface stale-value warnings prematurely). `null`
  // means "loaded — saved value was NULL". A string means the saved
  // value, used at submit time as a fallback when state is empty.
  const [outboundExternalCallerIdLoaded, setOutboundExternalCallerIdLoaded] =
    useState<string | null | undefined>(isAddMode ? null : undefined)

  useEffect(() => {
    if (isAddMode || !extension) return
    const line = extension as ExtensionInstance & LineSeedFields
    setName(line.name ?? '')
    setNumber(line.number ?? '')
    setLocationId(line.location?.id != null ? String(line.location.id) : '')
    setOutboundExtension(String(line.outbound_extension ?? '0'))
    setOutboundLocalAreaCode(line.outbound_local_area_code ?? '')
    const loadedCallerId =
      (line as LineSeedFields & { outbound_external_callerid?: string | null })
        .outbound_external_callerid ?? null
    setOutboundExternalCallerIdLoaded(loadedCallerId)
    outboundExternalCallerIdTouchedRef.current = false
    setOutboundExternalCallerId(loadedCallerId ?? '')
    setVoicemailEnabled(extension.voicemail_enabled === 1)
    setVoicemailTimeZone(
      normalizeToLegacyTimezone(
        line.voicemail_time_zone || DEFAULT_VOICEMAIL_TIMEZONE,
      ),
    )
    setVoicemailPassword(line.voicemail_password ?? '')
    setSendEmailNotifications(line.send_email_notifications ?? false)
    setAttachVoicemailMessage(line.attach_voicemail_message ?? false)
    setDeleteMessageAfterSend(line.delete_message_after_send ?? false)
    setMessageEmailAddress(line.message_email_address ?? '')
    setUseSystemGreetings(line.use_system_greetings ?? true)
    setOutgoingMessageUnavailableId(
      line.outgoing_message_unavailable_id != null
        ? String(line.outgoing_message_unavailable_id)
        : '',
    )
    setFindMeMode(normalizeFindMeMode(line.findme_mode))
    setFindMeDelay(normalizeFindMeDelay(line.findme_delay))
    setFindMeCellphone(line.findme_cellphone ?? '')
    setOmitFromDirectory(line.omit_from_directory ?? false)
    setForwardingNumber(line.forwarding_number ?? '')
    setLocalAreaCodeTouched(false)
    setT38Device(Boolean(line.t38_device))
    setPreferredCodec(line.preferred_codec ?? 'OPUS')
    setInCallStarCodesEnabled(Boolean(line.in_call_star_codes_enabled))
    setCallRecording(
      line.call_recording == null
        ? '2'
        : String(
            typeof line.call_recording === 'boolean'
              ? line.call_recording
                ? 1
                : 0
              : line.call_recording,
          ),
    )
    setDialNowSummary(
      formatDialNowSummary(
        (
          line as LineSeedFields & {
            dial_now?:
              | string
              | {
                  cellular?: string | null
                  dial_now?: string | null
                }
              | null
          }
        ).dial_now,
      ),
    )
  }, [extension, isAddMode])

  useEffect(() => {
    if (isAddMode || !extension || isLoadingProfiles) return
    // Seed the assigned profile exactly once per extension. Re-running on a
    // later `pbxProfiles` reference change (e.g. a background refetch) would
    // clobber an unsaved in-progress selection back to the saved value. The
    // ref is only pinned once we've actually committed a value from a reliable
    // source, so a disabled/not-yet-populated profiles query (react-query
    // reports isLoading:false when disabled) can't lock in an empty selection.
    if (assignedProfileSeededKeyRef.current === extension.id) return
    const line = extension as ExtensionInstance & LineSeedFields
    if (line.assigned_profile?.id != null) {
      // Doesn't depend on pbxProfiles: profileOptions merges the saved profile
      // (above) so this value always matches a rendered option.
      assignedProfileSeededKeyRef.current = extension.id
      setAssignedProfileId(String(line.assigned_profile.id))
    } else if (line.assigned_user?.id != null) {
      // Legacy line with only a user reference: we can only map it once the
      // profile list is actually loaded. Wait (don't pin) until then so the
      // effect re-runs when profiles arrive instead of committing ''.
      if (pbxProfiles.length === 0) return
      const matchingProfile = pbxProfiles.find(
        (p) => p.subject_id === line.assigned_user?.id,
      )
      assignedProfileSeededKeyRef.current = extension.id
      setAssignedProfileId(matchingProfile ? String(matchingProfile.id) : '')
    } else {
      // No assignment on the line.
      assignedProfileSeededKeyRef.current = extension.id
      setAssignedProfileId('')
    }
  }, [extension, isAddMode, pbxProfiles, isLoadingProfiles])

  useEffect(() => {
    if (isAddMode || !extension || !isMangoInternal) return
    setIsLineDetailsCollapsed(true)
  }, [isAddMode, extension?.id, isMangoInternal])

  useEffect(() => {
    if (!isAddMode || isLoadingAllExtensions) return
    setNumber((prev) => {
      if (prev !== '') return prev
      const sameTypeNumbers = allExtensions
        .filter((ext) => ext.type === ExtensionType.LINE)
        .map((ext) => ext.number)
      const allNumbers = allExtensions.map((ext) => ext.number)
      return getNextExtensionNumber(
        sameTypeNumbers,
        allNumbers,
        EXTENSION_TYPE_DEFAULT_NUMBER[type] ?? 100,
        isUkEnvironment,
      )
    })
  }, [isAddMode, isLoadingAllExtensions, allExtensions, type, isUkEnvironment])

  const pbxDefaultTimeZone = normalizeToLegacyTimezone(
    systemDefaults?.default_time_zone || DEFAULT_VOICEMAIL_TIMEZONE,
  )

  // Use PBX default timezone when creating a new line
  useEffect(() => {
    if (!isAddMode || !systemDefaults?.default_time_zone) return
    setVoicemailTimeZone(
      normalizeToLegacyTimezone(systemDefaults.default_time_zone),
    )
  }, [isAddMode, systemDefaults?.default_time_zone])

  const handleAssignedProfileChange = useCallback(
    (nextProfileId: string) => {
      setAssignedProfileId(nextProfileId)
      if (!nextProfileId) return
      const selectedProfile = profileOptions.find(
        (profile) => String(profile.id) === nextProfileId,
      )
      if (selectedProfile?.name) {
        setName(selectedProfile.name)
      }
    },
    [profileOptions],
  )

  useEffect(() => {
    // Wait for caller-ID options to actually finish loading before
    // attempting to reconcile the controlled state. The hook returns an
    // empty array both while pending and when a PBX legitimately has zero
    // numbers — without this gate the snap-to-first-option fallback below
    // would fire (or be skipped by the `length === 0` guard) before the
    // saved value has had a chance to match, and a save during that window
    // serialized `null` for `outbound_external_callerid`, breaking B-leg
    // call resolution downstream.
    if (isLoadingCallerIdOptions) return
    if (callerIdOptions.length === 0) return
    setOutboundExternalCallerId((currentValue) => {
      const exactMatch = callerIdOptions.find(
        (option) => option.value === currentValue,
      )
      if (exactMatch) return currentValue

      if (currentValue) {
        const normalizedMatch = callerIdOptions.find((option) =>
          phoneDigitsMatch(option.value, currentValue),
        )
        if (normalizedMatch) return normalizedMatch.value
      }

      const defaultCallerId = systemDefaults?.default_caller_id?.trim()
      if (defaultCallerId) {
        const matchingDefault =
          callerIdOptions.find((option) => option.value === defaultCallerId) ??
          callerIdOptions.find((option) =>
            phoneDigitsMatch(option.value, defaultCallerId),
          )
        if (matchingDefault) {
          return matchingDefault.value
        }
      }

      // Fallback: snap to the first option whenever the controlled value
      // doesn't match any rendered option. The native <select> has no
      // placeholder entry, so the browser visually selects the first
      // option in that case — without this sync, submit would serialize a
      // value the user can't see (or `null` when state is empty), which
      // we observed as "appears selected in the UI, but sends NULL". This
      // also covers the orphan case where a previously-saved phone number
      // has been removed from the PBX's caller-ID list.
      return callerIdOptions[0]?.value ?? ''
    })
    // Intentionally NOT depending on `outboundExtension`: the field
    // renders (and is required) regardless of OutDial state, so toggling
    // OutDial should not re-run the snap. Re-running on toggle would
    // overwrite a value the user just picked with the default.
    //
    // Depending on `outboundExternalCallerId` is what makes
    // `partialReset()` (Create-another flow) self-heal: the reset clears
    // state to `''` without changing options/defaults, and without this
    // dep the snap would not re-fire — leaving the dropdown visually
    // showing the first option (browser native fallback for an empty
    // controlled value) while state stays `''`, blocking submit. The
    // exact-match short-circuit above makes the effect a no-op (and
    // React bails on same-value setState) when state already matches an
    // option, so user picks are preserved.
  }, [
    callerIdOptions,
    systemDefaults?.default_caller_id,
    isLoadingCallerIdOptions,
    outboundExternalCallerId,
  ])

  useEffect(() => {
    const targetKey = isAddMode
      ? 'add'
      : extension
        ? `edit-${extension.id}`
        : null
    if (!targetKey) return
    if (localAreaCodeAutofilledKeyRef.current === targetKey) return
    if (outboundLocalAreaCode.trim() !== '') return

    if (!isAddMode && extension) {
      const savedAreaCode =
        (
          extension as ExtensionInstance & LineSeedFields
        ).outbound_local_area_code?.trim() ?? ''
      if (savedAreaCode !== '') {
        localAreaCodeAutofilledKeyRef.current = targetKey
        return
      }
    }

    const defaultAreaCode = systemDefaults?.default_area_code?.trim()
    if (!defaultAreaCode) return

    setOutboundLocalAreaCode(defaultAreaCode)
    localAreaCodeAutofilledKeyRef.current = targetKey
  }, [
    isAddMode,
    extension,
    outboundLocalAreaCode,
    systemDefaults?.default_area_code,
  ])

  // Note: we intentionally do NOT reset `sendEmailNotifications` when the
  // user toggles voicemail off. Clearing it lost the user's intent across
  // an off→on toggle (notifications silently flipped to off without
  // surfacing in the UI, since the toggle is hidden while voicemail is
  // disabled). State now holds intent; the submit payload below
  // normalizes the field to false when voicemail is off, and the
  // `emailRequiredButEmpty` check is also gated on `voicemailEnabled` so
  // an off-voicemail line with notifications=on doesn't fail email
  // validation for an unrelated field.
  const listPath = getListRoute({ pbxId: pbxIdNum, type })
  const typeLabel = getTypeLabelForSlug(type, text)
  const listLabel = getListLabelForSlug(type, text)

  const nameInvalid = attemptedSubmit && !name.trim()
  const numberInvalid = attemptedSubmit && !number.trim()
  const assignedProfileInvalid = attemptedSubmit && !assignedProfileId
  const locationInvalid = attemptedSubmit && !locationId
  const emailRequiredButEmpty =
    voicemailEnabled &&
    sendEmailNotifications &&
    messageEmailAddress.trim() === ''
  const emailFormatInvalid =
    messageEmailAddress.trim() !== '' && !isValidEmailList(messageEmailAddress)
  // Visual: surface format errors as soon as the user types an invalid
  // address (matches user expectation that the field "shows up" before submit).
  // The "required but empty" case stays gated by attemptedSubmit so the field
  // doesn't go red the moment the toggle is flipped.
  const emailInvalid =
    emailFormatInvalid || (attemptedSubmit && emailRequiredButEmpty)
  const forwardingNumberRawInvalid =
    outboundExtension === '1' &&
    (forwardingNumber.trim() === '' ||
      !FORWARDING_NUMBER_REGEX.test(
        forwardingNumber.trim().replace(/[\s-]/g, ''),
      ))
  const forwardingNumberInvalid = attemptedSubmit && forwardingNumberRawInvalid
  const normalizedFindMeCellphone =
    normalizeOptionalPhoneNumber(findMeCellphone)
  const findMeCellphoneInvalid =
    attemptedSubmit &&
    ((findMeMode === '1' && normalizedFindMeCellphone === '') ||
      (normalizedFindMeCellphone !== '' &&
        !FORWARDING_NUMBER_REGEX.test(normalizedFindMeCellphone)))
  const localAreaCodeBlocked = LEGACY_BLOCKED_AREA_CODES.has(
    outboundLocalAreaCode.trim(),
  )
  // Previously gated on `outboundExtension !== '1'` so OutDial=Yes lines
  // with a pre-existing blocked area code in the DB could still be saved
  // (the field was unmounted and the user couldn't clear the offending
  // value). Now that the Local Area Code field is always rendered, the
  // user can see and fix the blocked value, so the gate is no longer
  // needed and was dropped on purpose. This does mean an OutDial=Yes
  // line with a stale blocked area code now requires the user to clear
  // it before saving any other edit — that's the deliberate trade-off.
  const localAreaCodeInvalid =
    localAreaCodeBlocked && (attemptedSubmit || localAreaCodeTouched)
  // Phone team has confirmed `outbound_external_callerid` is *always*
  // required, including OutDial=Yes lines: the column is read by the call
  // pipeline as the From identity on the outbound leg, and a NULL there
  // breaks B-leg resolution. Validation must not depend on
  // `attemptedSubmit` — we want the user to see the field flagged the
  // moment the page loads if the saved value is missing (NULL in DB) or
  // doesn't match any option on this PBX (orphaned/legacy value).
  const noCallerIdOptionsConfigured =
    !isLoadingCallerIdOptions && callerIdOptions.length === 0
  // True when the loaded saved value was NULL (extension was never given
  // a caller ID) or when it couldn't be reconciled against any rendered
  // option (format mismatch, removed phone number). The snap effect will
  // have replaced state with a default/first-option in the unmatched
  // case, but we still want to warn the user that their *previously
  // saved* value was lost — until they pick a new value, at which point
  // the warning is no longer accurate and would just be confusing.
  //
  // Reading `touchedRef.current` during render is intentional: the
  // onChange handler that flips it also calls setOutboundExternalCallerId,
  // and that state update is what triggers the re-render where this
  // expression re-evaluates. The ref alone wouldn't schedule a render.
  const loadedCallerIdMissingOrUnmatched =
    !isAddMode &&
    !isLoadingCallerIdOptions &&
    !outboundExternalCallerIdTouchedRef.current &&
    callerIdOptions.length > 0 &&
    outboundExternalCallerIdLoaded !== undefined &&
    (outboundExternalCallerIdLoaded === null ||
      !callerIdOptions.some(
        (option) =>
          option.value === outboundExternalCallerIdLoaded ||
          phoneDigitsMatch(option.value, outboundExternalCallerIdLoaded ?? ''),
      ))
  // The `!isLoadingCallerIdOptions` gate covers the period before
  // options arrive. Once options arrive there is one render where state
  // is still `''` (snap effect hasn't committed yet) and this would
  // evaluate true. React runs the effect and schedules the next render
  // before paint, so the red flash should not be visible in practice;
  // if QA reports a flicker on slow machines we can add a stricter gate
  // (e.g. require the snap effect to have run at least once).
  const outboundCallerIdInvalid =
    !isLoadingCallerIdOptions &&
    (noCallerIdOptionsConfigured || !outboundExternalCallerId)

  const partialReset = useCallback(() => {
    setName('')
    setNumber('')
    setAssignedProfileId('')
    setLocationId('')
    setOutboundExtension('0')
    setOutboundLocalAreaCode('')
    setOutboundExternalCallerId('')
    setOutboundExternalCallerIdLoaded(null)
    outboundExternalCallerIdTouchedRef.current = false
    setVoicemailEnabled(true)
    setVoicemailTimeZone(pbxDefaultTimeZone)
    setVoicemailPassword(generateSecureFourDigitPin())
    setSendEmailNotifications(false)
    setAttachVoicemailMessage(false)
    setDeleteMessageAfterSend(false)
    setMessageEmailAddress('')
    setUseSystemGreetings(true)
    setOutgoingMessageUnavailableId('')
    setFindMeMode('0')
    setFindMeDelay(0)
    setFindMeCellphone('')
    setOmitFromDirectory(false)
    setForwardingNumber('')
    setLocalAreaCodeTouched(false)
    setT38Device(false)
    setPreferredCodec('OPUS')
    setInCallStarCodesEnabled(true)
    setCallRecording('2')
    setAttemptedSubmit(false)
    localAreaCodeAutofilledKeyRef.current = null
    requestAnimationFrame(() => nameInputRef.current?.focus())
  }, [pbxDefaultTimeZone])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const stayOnPage = createAnotherRef.current
    createAnotherRef.current = false
    setAttemptedSubmit(true)
    if (!name.trim() || !number.trim() || !assignedProfileId || !locationId) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.line.requiredFields',
          'Name, number, location, and assigned profile are required.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (
      isReservedExtensionNumber(number.trim(), { isUkEnvironment }) &&
      (isAddMode || number.trim() !== (extension?.number ?? '').trim())
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.reservedNumber',
          '{{number}} is reserved. Please select a different number.',
          { number: number.trim() },
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (findMeCellphoneInvalid) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description:
          findMeMode === '1' && normalizedFindMeCellphone === ''
            ? text(
                'extensions.edit.line.findMe.numberRequired',
                'A Find Me Number must be given if Find Me is On.',
              )
            : text(
                'extensions.edit.line.findMe.numberInvalid',
                'Find Me Number must contain only digits and an optional leading + (max 15 digits).',
              ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    // Use the un-gated raw checks here. `emailInvalid` /
    // `forwardingNumberInvalid` both depend on `attemptedSubmit`, but
    // `setAttemptedSubmit(true)` above only schedules a re-render — the
    // closure in this handler still sees the previous value, so checking the
    // gated flags would let the first invalid submit slip through.
    if (
      emailFormatInvalid ||
      emailRequiredButEmpty ||
      forwardingNumberRawInvalid
    ) {
      let description = text(
        'extensions.edit.reviewRequiredFields',
        'Please review required fields and try again.',
      )
      if (emailFormatInvalid) {
        description = text(
          'extensions.edit.emailFormatInvalid',
          'Incorrect email formatting. Make sure there are no spaces. Separate multiple addresses with commas.',
        )
      } else if (emailRequiredButEmpty) {
        description = text(
          'extensions.edit.line.emailRequired',
          'Please enter an Email Address.',
        )
      }
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description,
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (localAreaCodeBlocked) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.line.localAreaCodeNotAllowed',
          'The Area Code {{areaCode}} is not permitted for the Local Area Code.',
          { areaCode: outboundLocalAreaCode.trim() },
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (outboundCallerIdInvalid) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: noCallerIdOptionsConfigured
          ? text(
              'extensions.edit.line.outboundCallerIdNoOptions',
              'This PBX has no phone numbers configured. Add a phone number to the PBX before editing this line.',
            )
          : text(
              'extensions.edit.line.outboundCallerIdRequired',
              'Please select an Outbound Caller ID.',
            ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }

    // When the user explicitly changed the select this session, use their
    // pick. When untouched, fall back to current state then the originally
    // loaded server value so an unrelated edit can't drop the caller ID.
    const resolvedOutboundExternalCallerId =
      outboundExternalCallerIdTouchedRef.current
        ? outboundExternalCallerId || null
        : outboundExternalCallerId || outboundExternalCallerIdLoaded || null

    const payload: UpdateExtensionPayload = {
      name: name.trim(),
      number: number.trim(),
      assigned_voip_profile_id: parseInt(assignedProfileId, 10),
      location_id: locationId ? parseInt(locationId, 10) : null,
      outbound_extension: outboundExtension === '1' ? 1 : 0,
      // OutDial toggle controls field *visibility*, not persistence. Always
      // send the current state value for outbound_local_area_code,
      // outbound_external_callerid, and forwarding_number so toggling OutDial
      // off and back on does not erase previously saved values (matches
      // legacy admin behavior in Pbx_model::editPbxLine).
      outbound_local_area_code: outboundLocalAreaCode.trim() || null,
      voicemail_enabled: voicemailEnabled,
      voicemail_time_zone: voicemailTimeZone || null,
      voicemail_password: voicemailPassword || null,
      // Normalize at the boundary: state preserves the user's intent
      // across a voicemail off/on toggle, but the persisted value can't
      // be true when voicemail itself is off.
      send_email_notifications: voicemailEnabled && sendEmailNotifications,
      // The toggles are only rendered when voicemail and email notifications
      // are both on. Omit the keys otherwise so an unrelated save can't zero
      // previously stored values (the backend applies only keys present).
      message_email_address: messageEmailAddress || null,
      use_system_greetings: useSystemGreetings,
      // Only carries a clip when a custom greeting is selected; switching back
      // to system greetings clears the stored clip.
      outgoing_message_unavailable_id:
        !useSystemGreetings && outgoingMessageUnavailableId.trim()
          ? parseInt(outgoingMessageUnavailableId, 10)
          : null,
      findme_mode: findMeMode,
      findme_delay: findMeDelay,
      findme_cellphone: normalizedFindMeCellphone || null,
      omit_from_directory: omitFromDirectory,
      forwarding_number: forwardingNumber.trim().replace(/[\s-]/g, '') || null,
      call_recording: parseInt(callRecording, 10),
      // Only include `outbound_external_callerid` when we have a value to
      // write. The backend applies only keys present in the request, so
      // omitting it preserves the existing DB value instead of nulling it
      // (a NULL breaks B-leg call resolution downstream).
      ...(resolvedOutboundExternalCallerId != null
        ? { outbound_external_callerid: resolvedOutboundExternalCallerId }
        : {}),
      ...(voicemailEnabled && sendEmailNotifications
        ? {
            attach_voicemail_message: attachVoicemailMessage,
            delete_message_after_send: deleteMessageAfterSend,
          }
        : {}),
      ...(isMangoInternal
        ? {
            t38_device: t38Device ? 1 : 0,
            preferred_codec: preferredCodec || null,
            in_call_star_codes_enabled: inCallStarCodesEnabled,
          }
        : {}),
    }

    const onSuccess = () => {
      toast.create({
        title: isAddMode
          ? text('extensions.edit.line.created', 'Line created')
          : text('extensions.edit.line.updated', 'Line updated'),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
      if (stayOnPage) {
        partialReset()
      } else {
        navigate(listPath)
      }
    }
    const onError = (error: unknown) => {
      const apiErr = error as ApiError | undefined
      toast.create({
        title: isAddMode
          ? text('extensions.edit.createFailed', 'Create failed')
          : text('extensions.edit.updateFailed', 'Update failed'),
        description: apiErr?.message ?? (error as Error)?.message,
        type: 'error',
        duration: 5000,
        closable: true,
      })
    }

    if (isAddMode) {
      // outbound_external_callerid is required on create (the backend rejects
      // the line otherwise), so it must be in the create body — not deferred to
      // the follow-up update, which never runs if create 400s.
      createMutation.mutate(
        buildLineCreatePayload({
          name: name.trim(),
          number: number.trim(),
          assignedProfileId: parseInt(assignedProfileId, 10),
          locationId: locationId ? parseInt(locationId, 10) : null,
          outboundExternalCallerId: resolvedOutboundExternalCallerId,
        }) as never,
        {
          onSuccess: (created) => {
            updateMutation.mutate(
              { extensionId: created.id, payload },
              { onSuccess, onError },
            )
          },
          onError,
        },
      )
      return
    }

    if (!extensionId) return
    updateMutation.mutate({ extensionId, payload }, { onSuccess, onError })
  }

  const isPending = updateMutation.isPending || createMutation.isPending
  const breadcrumbs = [
    { label: listLabel, path: listPath },
    {
      label: isAddMode
        ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
            typeLabel,
          })
        : text(
            'extensions.edit.breadcrumbEdit',
            'Edit {{typeLabel}} {{value}}',
            { typeLabel, value: extension?.number ?? extension?.id ?? '' },
          ),
    },
  ]

  if (!isAddMode && (isLoading || !extension)) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" />
      </Box>,
    )
  }
  if (!isAddMode && (isError || extension?.type !== ExtensionType.LINE)) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {text('extensions.edit.line.notType', 'Not a line')}
          </Heading>
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel,
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  const content = (
    <Box px={{ base: 3, md: 5 }} py={{ base: 3, md: 4 }}>
      {!isAddMode && extension && isMangoInternal && (
        <>
          <Card.Root mb={4}>
            <Card.Header p={5}>
              <FlexHeader
                extension={extension}
                isCollapsed={isLineDetailsCollapsed}
                onToggleCollapse={() =>
                  setIsLineDetailsCollapsed((prev) => !prev)
                }
                onToggleActive={() => {
                  const isActive = !isInactiveExtension(extension)
                  setActiveStateDialogTarget(!isActive)
                  setActiveStateDialogOpen(true)
                }}
                isPending={setActiveStateMutation.isPending}
              />
            </Card.Header>
            {!isLineDetailsCollapsed && (
              <Card.Body p={5}>
                <Box
                  display="grid"
                  gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
                  gap={4}
                >
                  <Box>
                    <Text color="fg.subtle" fontWeight="medium" mb={1}>
                      {text('extensions.edit.line.pbxUuid', 'PBX UUID')}
                    </Text>
                    <Text fontFamily="mono" fontSize="xs">
                      {extension.pbx?.uuid || '—'}
                    </Text>
                  </Box>
                  <Box>
                    <Text color="fg.subtle" fontWeight="medium" mb={1}>
                      {text('extensions.edit.line.pbxName', 'PBX')}
                    </Text>
                    <Text>{extension.pbx?.name || '—'}</Text>
                  </Box>
                  <Box>
                    <Text color="fg.subtle" fontWeight="medium" mb={1}>
                      {text('extensions.edit.line.sipSecret', 'SIP Secret')}
                    </Text>
                    <HStack gap={2}>
                      <Text fontFamily="mono" fontSize="xs">
                        {(
                          extension as ExtensionInstance & {
                            sip_secret?: string
                          }
                        ).sip_secret || '—'}
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() =>
                          resetSipSecretMutation.mutate(undefined, {
                            onSuccess: () =>
                              toast.create({
                                title: text(
                                  'extensions.edit.line.sipSecretReset',
                                  'SIP secret reset',
                                ),
                                type: 'success',
                                duration: 5000,
                                closable: true,
                              }),
                            onError: (error: unknown) =>
                              toast.create({
                                title: text(
                                  'extensions.edit.line.sipSecretResetFailed',
                                  'SIP secret reset failed',
                                ),
                                description: (error as Error)?.message,
                                type: 'error',
                                duration: 5000,
                                closable: true,
                              }),
                          })
                        }
                      >
                        {text('extensions.edit.line.reset', 'Reset')}
                      </Button>
                    </HStack>
                  </Box>
                  <Box>
                    <Text color="fg.subtle" fontWeight="medium" mb={1}>
                      {text(
                        'extensions.edit.line.extensionUuid',
                        'Extension UUID',
                      )}
                    </Text>
                    <Text fontFamily="mono" fontSize="xs">
                      {extension.uuid || '—'}
                    </Text>
                  </Box>
                  <Box>
                    <Text color="fg.subtle" fontWeight="medium" mb={1}>
                      {text('extensions.edit.line.dialNow', 'Dial Now')}
                    </Text>
                    <Text fontSize="sm" color="fg.muted" mb={2}>
                      {dialNowSummary || 'N/A'}
                    </Text>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        clearDialNowMutation.mutate(undefined, {
                          onSuccess: () => {
                            setDialNowSummary('')
                            toast.create({
                              title: text(
                                'extensions.edit.line.dialNowCleared',
                                'Dial-now cleared',
                              ),
                              type: 'success',
                              duration: 5000,
                              closable: true,
                            })
                          },
                        })
                      }
                      disabled={!dialNowSummary}
                    >
                      {text('extensions.edit.line.clear', 'Clear')}
                    </Button>
                  </Box>
                  <Box gridColumn={{ base: '1 / -1', md: '1 / -1' }}>
                    <Text color="fg.subtle" fontWeight="medium" mb={2}>
                      {text(
                        'extensions.edit.line.assignedProfileMobileDevices',
                        "Assigned Profile's Mobile Devices",
                      )}
                    </Text>
                    {mobileDevices.length > 0 ? (
                      <HStack gap={2} wrap="wrap">
                        {mobileDevices.map((device) => (
                          <Badge
                            key={device.id}
                            variant="outline"
                            title={`${device.device_os} • v${device.application_version}`}
                          >
                            {device.device_name}
                          </Badge>
                        ))}
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="fg.muted">
                        {text('extensions.common.none', 'None')}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Card.Body>
            )}
          </Card.Root>
          <SetActiveStateDialog
            isOpen={activeStateDialogOpen}
            onClose={() => setActiveStateDialogOpen(false)}
            targetActive={activeStateDialogTarget}
            displayName={extension?.name ?? extension?.number ?? ''}
            onConfirm={() =>
              setActiveStateMutation.mutate(activeStateDialogTarget, {
                onSuccess: () => setActiveStateDialogOpen(false),
              })
            }
            isPending={setActiveStateMutation.isPending}
          />
        </>
      )}

      <form onSubmit={handleSubmit}>
        <VStack gap={6} align="stretch" css={SWITCH_FORM_CSS}>
          <Card.Root>
            <Card.Header p={4}>
              <HStack justify="space-between" align="center">
                <Card.Title>
                  {isAddMode
                    ? text('extensions.edit.line.addTitle', 'Add Line')
                    : text('extensions.edit.line.title', 'Line')}
                </Card.Title>
                {!isAddMode &&
                  !isMangoInternal &&
                  extension &&
                  isInactiveExtension(extension) && (
                    <Badge colorPalette="error" size="sm">
                      {text('extensions.filters.inactive', 'Inactive')}
                    </Badge>
                  )}
              </HStack>
            </Card.Header>
            <Card.Body p={6} pt={0}>
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
                <Field.Root required invalid={locationInvalid}>
                  <Field.Label>
                    {text('extensions.filters.location', 'Location')}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                    >
                      <option value="">
                        {text(
                          'extensions.edit.line.selectLocation',
                          'Select A Location',
                        )}
                      </option>
                      {pbxLocationOptions.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Field.Root>
                  <HStack gap={2} align="center">
                    <Field.Label mb={0}>
                      {text(
                        'extensions.edit.line.outDialExtension',
                        'OutDial Extension',
                      )}
                    </Field.Label>
                    <Tooltip.Root openDelay={250} closeDelay={150} interactive>
                      <Tooltip.Trigger asChild>
                        <Button
                          type="button"
                          size="2xs"
                          variant="outline"
                          borderRadius="999px"
                          minW="18px"
                          h="18px"
                          px={0}
                          aria-label={text(
                            'extensions.edit.line.outDialHelp',
                            'OutDial extension help',
                          )}
                        >
                          ?
                        </Button>
                      </Tooltip.Trigger>
                      <Portal>
                        <Tooltip.Positioner>
                          <Tooltip.Content maxW="320px">
                            <Text fontSize="xs" mb={1}>
                              {text(
                                'extensions.edit.line.outDialHelpNo',
                                'No: This extension acts as a regular extension.',
                              )}
                            </Text>
                            <Text fontSize="xs" mb={1}>
                              {text(
                                'extensions.edit.line.outDialHelpYes',
                                'Yes: This extension only forwards to the Forwarding Number.',
                              )}
                            </Text>
                            <Text fontSize="xs" fontWeight="medium" mb={1}>
                              {text(
                                'extensions.edit.line.outDialHelpBeAware',
                                'Be aware:',
                              )}
                            </Text>
                            <Text fontSize="xs">
                              {text(
                                'extensions.edit.line.outDialHelpWarningOne',
                                'The system cannot retrieve a call once it has forwarded to an outside number.',
                              )}
                            </Text>
                            <Text fontSize="xs">
                              {text(
                                'extensions.edit.line.outDialHelpWarningTwo',
                                'You cannot use system voicemail with an OutDial extension.',
                              )}
                            </Text>
                          </Tooltip.Content>
                        </Tooltip.Positioner>
                      </Portal>
                    </Tooltip.Root>
                  </HStack>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={outboundExtension}
                      onChange={(e) => setOutboundExtension(e.target.value)}
                    >
                      <option value="1">
                        {text('extensions.edit.yes', 'Yes')}
                      </option>
                      <option value="0">
                        {text('extensions.edit.no', 'No')}
                      </option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  <Text fontSize="xs" color="fg.muted" mt={1}>
                    {outboundExtension === '1'
                      ? text(
                          'extensions.edit.line.outDialEnabledHint',
                          'OutDial is enabled. Calls forward to the Forwarding Number; Outbound Caller ID still applies on the outbound leg.',
                        )
                      : text(
                          'extensions.edit.line.outDialDisabledHint',
                          'OutDial is disabled. Outbound caller settings apply.',
                        )}
                  </Text>
                </Field.Root>
                <Field.Root required invalid={assignedProfileInvalid}>
                  <Field.Label>
                    {text(
                      'extensions.filters.assignedProfile',
                      'Assigned profile',
                    )}
                  </Field.Label>
                  <NativeSelect.Root disabled={isLoadingProfiles}>
                    {isLoadingProfiles ? (
                      <NativeSelect.Field value="">
                        <option value="">
                          {text(
                            'extensions.edit.line.loadingProfiles',
                            'Loading profiles…',
                          )}
                        </option>
                      </NativeSelect.Field>
                    ) : (
                      <NativeSelect.Field
                        value={assignedProfileId}
                        onChange={(e) =>
                          handleAssignedProfileChange(e.target.value)
                        }
                      >
                        <option value="">
                          {text('extensions.common.none', 'None')}
                        </option>
                        {profileOptions.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.name}
                          </option>
                        ))}
                      </NativeSelect.Field>
                    )}
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Field.Root required invalid={nameInvalid}>
                  <Field.Label>
                    {text('extensions.columns.name', 'Name')}
                  </Field.Label>
                  <Input
                    ref={nameInputRef}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Field.Root>
                <Field.Root required invalid={numberInvalid}>
                  <Field.Label>
                    {text('extensions.columns.number', 'Number')}
                  </Field.Label>
                  <Input
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    required
                  />
                </Field.Root>
                {outboundExtension === '1' && (
                  <Field.Root required invalid={forwardingNumberInvalid}>
                    <Field.Label>
                      {text(
                        'extensions.edit.line.forwardingNumber',
                        'Forwarding number (OutDial)',
                      )}
                    </Field.Label>
                    <Input
                      value={forwardingNumber}
                      onChange={(e) => setForwardingNumber(e.target.value)}
                    />
                  </Field.Root>
                )}
              </Grid>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Header p={4}>
              <Card.Title>
                {text('extensions.edit.line.findMe.title', 'Find Me')}
              </Card.Title>
            </Card.Header>
            <Card.Body p={6} pt={0}>
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
                <Field.Root>
                  <Field.Label>
                    {text('extensions.edit.line.findMe.mode', 'Find Me')}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={findMeMode}
                      onChange={(e) => setFindMeMode(e.target.value)}
                    >
                      {FINDME_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {text(
                            `extensions.edit.line.findMe.modeOptions.${option.value}`,
                            option.label,
                          )}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Field.Root>
                  <Field.Label>
                    {text('extensions.edit.line.findMe.delay', 'Find Me After')}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={String(findMeDelay)}
                      onChange={(e) =>
                        setFindMeDelay(parseInt(e.target.value, 10) || 0)
                      }
                    >
                      {FINDME_DELAY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {text(
                            `extensions.edit.line.findMe.delayOptions.${option.value}`,
                            option.label,
                          )}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Field.Root
                  required={findMeMode === '1'}
                  invalid={findMeCellphoneInvalid}
                >
                  <Field.Label>
                    {text(
                      'extensions.edit.line.findMe.number',
                      'Find Me Number',
                    )}
                  </Field.Label>
                  <Input
                    value={findMeCellphone}
                    onChange={(e) => setFindMeCellphone(e.target.value)}
                  />
                </Field.Root>
              </Grid>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Header p={4}>
              <Card.Title>
                {text('extensions.edit.options', 'Options')}
              </Card.Title>
            </Card.Header>
            <Card.Body p={6} pt={0}>
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
                <Field.Root invalid={localAreaCodeInvalid}>
                  <Field.Label>
                    {text(
                      'extensions.edit.line.localAreaCode',
                      'Local Area Code',
                    )}
                  </Field.Label>
                  <Input
                    value={outboundLocalAreaCode}
                    onChange={(e) => setOutboundLocalAreaCode(e.target.value)}
                    onBlur={() => setLocalAreaCodeTouched(true)}
                  />
                  {localAreaCodeInvalid && (
                    <Field.ErrorText>
                      {text(
                        'extensions.edit.line.localAreaCodeInvalid',
                        'Area code is invalid. {{areaCode}} is not permitted for the Local Area Code.',
                        { areaCode: outboundLocalAreaCode.trim() },
                      )}
                    </Field.ErrorText>
                  )}
                </Field.Root>
                <Field.Root
                  required
                  invalid={
                    outboundCallerIdInvalid || loadedCallerIdMissingOrUnmatched
                  }
                >
                  <Field.Label>
                    {text(
                      'extensions.massEdit.outboundCallerId',
                      'Outbound caller ID',
                    )}
                  </Field.Label>
                  <NativeSelect.Root disabled={noCallerIdOptionsConfigured}>
                    <NativeSelect.Field
                      value={outboundExternalCallerId}
                      onChange={(e) => {
                        outboundExternalCallerIdTouchedRef.current = true
                        setOutboundExternalCallerId(e.target.value)
                      }}
                    >
                      {callerIdOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  {noCallerIdOptionsConfigured && (
                    <Field.ErrorText>
                      {text(
                        'extensions.edit.line.outboundCallerIdNoOptions',
                        'This PBX has no phone numbers configured. Add a phone number to the PBX before editing this line.',
                      )}
                    </Field.ErrorText>
                  )}
                  {!noCallerIdOptionsConfigured &&
                    loadedCallerIdMissingOrUnmatched && (
                      <Field.ErrorText>
                        {text(
                          'extensions.edit.line.outboundCallerIdMissingSavedValue',
                          'This line is missing a valid Outbound Caller ID. Please select one and save before continuing.',
                        )}
                      </Field.ErrorText>
                    )}
                </Field.Root>
                <Field.Root alignSelf="center">
                  <Toggle
                    colorPalette="brand"
                    checked={omitFromDirectory}
                    onCheckedChange={(e) => setOmitFromDirectory(e.checked)}
                  >
                    {text(
                      'extensions.edit.omitFromDirectory',
                      'Omit from directory',
                    )}
                  </Toggle>
                </Field.Root>
                {isMangoInternal && (
                  <>
                    <Field.Root>
                      <Field.Label>
                        {text(
                          'extensions.edit.line.dedicatedFax',
                          'Dedicated Fax',
                        )}
                      </Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={t38Device ? '1' : '0'}
                          onChange={(e) => setT38Device(e.target.value === '1')}
                        >
                          <option value="1">
                            {text('extensions.edit.yes', 'Yes')}
                          </option>
                          <option value="0">
                            {text('extensions.edit.no', 'No')}
                          </option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>
                        {text(
                          'extensions.massEdit.preferredCodec',
                          'Preferred codec',
                        )}
                      </Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={preferredCodec}
                          onChange={(e) => setPreferredCodec(e.target.value)}
                        >
                          {LEGACY_PREFERRED_CODEC_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.value === 'OPUS'
                                ? text(
                                    'extensions.edit.line.codecOpus',
                                    'Best for slow internet (OPUS) Supports: Web Phones, Yealink',
                                  )
                                : option.value === 'PCMU'
                                  ? text(
                                      'extensions.edit.line.codecPcmu',
                                      'Standard (PCMU) Supports: All phone types',
                                    )
                                  : option.value === 'G722'
                                    ? text(
                                        'extensions.edit.line.codecG722',
                                        'HD Audio (G722) Supports: Yealink, Cisco',
                                      )
                                    : text(
                                        'extensions.edit.line.codecG729',
                                        'DEPRECATED - Good for slow internet (G729) Supports: Yealink, Cisco',
                                      )}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>
                        {text(
                          'extensions.edit.line.inCallFeatureCodes',
                          'In-Call Feature Codes Enabled',
                        )}
                      </Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={inCallStarCodesEnabled ? '1' : '0'}
                          onChange={(e) =>
                            setInCallStarCodesEnabled(e.target.value === '1')
                          }
                        >
                          <option value="1">
                            {text('extensions.edit.yes', 'Yes')}
                          </option>
                          <option value="0">
                            {text('extensions.edit.no', 'No')}
                          </option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  </>
                )}
                <Field.Root>
                  <Field.Label>
                    {text(
                      'extensions.edit.line.callRecording',
                      'Call Recording',
                    )}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={callRecording}
                      onChange={(e) => setCallRecording(e.target.value)}
                    >
                      {CALL_RECORDING_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {text(
                            `extensions.edit.line.callRecordingOptions.${option.value}`,
                            option.label,
                          )}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </Grid>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Header p={4}>
              <HStack justify="flex-start" alignItems="baseline">
                <Card.Title pr={4}>
                  {text('extensions.edit.line.voicemailOptions', 'Voicemail')}
                </Card.Title>
                <Toggle
                  colorPalette="brand"
                  checked={voicemailEnabled}
                  onCheckedChange={(e) => setVoicemailEnabled(e.checked)}
                >
                  {text(
                    'extensions.edit.line.voicemailEnabled',
                    'Voicemail enabled',
                  )}
                </Toggle>
              </HStack>
            </Card.Header>
            {voicemailEnabled && (
              <Card.Body p={6} pt={0}>
                <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
                  <Field.Root>
                    <Field.Label>
                      {text(
                        'extensions.edit.shared.voicemailTimeZone',
                        'Voicemail time zone',
                      )}
                    </Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={voicemailTimeZone}
                        onChange={(e) => setVoicemailTimeZone(e.target.value)}
                      >
                        {VOICEMAIL_TIMEZONE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {text(
                              `extensions.timezones.${toI18nKey(option.value)}`,
                              option.label,
                            )}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>
                      {text(
                        'extensions.edit.shared.voicemailPassword',
                        'Voicemail password',
                      )}
                    </Field.Label>
                    <Input
                      value={voicemailPassword}
                      onChange={(e) => setVoicemailPassword(e.target.value)}
                    />
                  </Field.Root>
                  <Field.Root
                    alignSelf="flex-start"
                    alignItems="flex-start"
                    gridColumn={1}
                  >
                    <Toggle
                      colorPalette="brand"
                      checked={sendEmailNotifications}
                      onCheckedChange={(e) =>
                        setSendEmailNotifications(e.checked)
                      }
                    >
                      {text(
                        'extensions.edit.shared.sendEmailNotifications',
                        'Send email notifications',
                      )}
                    </Toggle>
                  </Field.Root>
                  {sendEmailNotifications && (
                    <>
                      <Field.Root
                        required
                        invalid={emailInvalid}
                        gridColumn={2}
                      >
                        <Field.Label>
                          {text(
                            'extensions.edit.shared.messageEmailAddress',
                            'Message email address',
                          )}
                        </Field.Label>
                        <Input
                          value={messageEmailAddress}
                          onChange={(e) =>
                            setMessageEmailAddress(e.target.value)
                          }
                        />
                      </Field.Root>
                    </>
                  )}
                  {voicemailEnabled && sendEmailNotifications && (
                    <>
                      <Field.Root alignSelf="center" gridColumn={1}>
                        <Toggle
                          colorPalette="brand"
                          checked={attachVoicemailMessage}
                          onCheckedChange={(e) =>
                            setAttachVoicemailMessage(e.checked === true)
                          }
                        >
                          {text(
                            'extensions.edit.shared.attachVoicemailMessage',
                            'Attach voicemail message',
                          )}
                        </Toggle>
                      </Field.Root>
                      <Field.Root alignSelf="center" gridColumn={2}>
                        <Toggle
                          colorPalette="brand"
                          checked={deleteMessageAfterSend}
                          onCheckedChange={(e) =>
                            setDeleteMessageAfterSend(e.checked === true)
                          }
                        >
                          {text(
                            'extensions.edit.shared.deleteMessageAfterSend',
                            'Delete message after send',
                          )}
                        </Toggle>
                      </Field.Root>
                    </>
                  )}
                  <Field.Root
                    alignSelf="flex-start"
                    alignItems="flex-start"
                    gridColumn={1}
                  >
                    <Toggle
                      colorPalette="brand"
                      checked={useSystemGreetings}
                      onCheckedChange={(e) =>
                        setUseSystemGreetings(e.checked === true)
                      }
                    >
                      {text(
                        'extensions.edit.shared.useSystemGreetings',
                        'Use system greetings',
                      )}
                    </Toggle>
                  </Field.Root>
                  {!useSystemGreetings && (
                    <Field.Root gridColumn={2}>
                      <Field.Label>
                        {text(
                          'extensions.edit.shared.customGreeting',
                          'Custom Greeting',
                        )}
                      </Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={outgoingMessageUnavailableId}
                          onChange={(e) =>
                            setOutgoingMessageUnavailableId(e.target.value)
                          }
                        >
                          <option value="">
                            {text('extensions.common.none', 'None')}
                          </option>
                          {soundClips.map((sound) => (
                            <option key={sound.id} value={String(sound.id)}>
                              {sound.name}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  )}
                </Grid>
              </Card.Body>
            )}
          </Card.Root>

          <Box display="flex" gap={3} pt={2} px={4} pb={3}>
            <Button
              type="submit"
              colorPalette="brand"
              loading={isPending}
              borderRadius="4px"
            >
              {isAddMode
                ? text('extensions.edit.line.createButton', 'Create Line')
                : text('extensions.edit.saveChanges', 'Save changes')}
            </Button>
            {isAddMode && (
              <Button
                type="submit"
                variant="outline"
                colorPalette="brand"
                borderRadius="4px"
                loading={isPending}
                onClick={() => {
                  // onClick fires before the form's onSubmit per browser spec
                  createAnotherRef.current = true
                }}
              >
                {text(
                  'extensions.edit.line.saveAndCreateAnother',
                  'Save & Create Another',
                )}
              </Button>
            )}
            <Button type="button" variant="outline" borderRadius="4px" asChild>
              <Link to={listPath}>
                {text('extensions.common.cancel', 'Cancel')}
              </Link>
            </Button>
          </Box>
        </VStack>
      </form>
    </Box>
  )

  return renderWithWrapper(wrapper, breadcrumbs, content)
}

const FlexHeader = ({
  extension,
  isCollapsed,
  onToggleCollapse,
  onToggleActive,
  isPending,
}: {
  extension: ExtensionInstance
  isCollapsed: boolean
  onToggleCollapse: () => void
  onToggleActive: () => void
  isPending: boolean
}) => {
  const { text } = useExtensionsI18n()
  const isActive = !isInactiveExtension(extension)
  return (
    <HStack justify="space-between" align="center">
      <HStack gap={2}>
        <Heading size="md">
          {text('extensions.edit.line.details', 'Line Details')}
        </Heading>
        <Button
          size="xs"
          variant="outline"
          bg="bg.default"
          borderRadius="4px"
          onClick={onToggleCollapse}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed
            ? text('extensions.edit.line.expandDetails', 'Show details')
            : text('extensions.edit.line.collapseDetails', 'Hide details')}
        </Button>
      </HStack>
      <HStack gap={2}>
        <Badge colorPalette={isActive ? 'success' : 'error'} size="sm">
          {isActive
            ? text('extensions.filters.active', 'Active')
            : text('extensions.filters.inactive', 'Inactive')}
        </Badge>
        <Button
          size="xs"
          variant="outline"
          onClick={onToggleActive}
          loading={isPending}
        >
          {isActive
            ? text('extensions.edit.line.deactivate', 'Deactivate')
            : text('extensions.edit.line.activate', 'Activate')}
        </Button>
      </HStack>
    </HStack>
  )
}
